import { map } from "lodash";
import { Col, Image, Row } from "react-bootstrap";
import { useWatch } from "react-hook-form";
import { useProducts } from "../../../adapters/uplist";
import { PREMIUM_FP_ADDON_IMAGES } from "../../../assets/images/samples";
import { useFormDetailsState } from "../../../context/FormDetailsContext";
import { isInternal } from "../../../helpers";
import {
  FURNISHED_FP_SKUS,
  GARDEN_FP_SKUS,
  PREMIUM_FP_ADDON_SKUS,
  TEXTURED_FP_SKUS,
} from "../../../helpers/constants";
import { useSetPriceBySqft, useWindowSize } from "../../../hooks";
import { BlockCheckbox, Button, InlineCheckbox } from "../../fields";
import AddOnSection from "../AddOnSection";
import { FurnishedFpProduct } from "./FurnishedFpProduct";

const SKU_GROUPS = {
  textured: { skus: TEXTURED_FP_SKUS, name_prefix: "Textured Floor Plan - " },
  garden: { skus: GARDEN_FP_SKUS, name_prefix: "Garden Plan - " },
};
export const PREMIUM_ADDON_SKU_KEYS = {
  2017: "F",
  2018: "FM",
  2019: "FR",
  2020: "GM",
  2021: "GR",
};
const KEY_ORDER = ["FM", "FR", "GM", "GR", "F"];

const useAddonLetter = (skus) => {
  const values = useWatch({ name: skus });
  const selected_sku = skus[values.findIndex((s) => !!s)];
  return PREMIUM_ADDON_SKU_KEYS[selected_sku];
};

const useSelectedAddons = () => {
  const gardenLetter = useAddonLetter(GARDEN_FP_SKUS);
  const texturedLetter = useAddonLetter(TEXTURED_FP_SKUS);
  const furnishedLetter = useAddonLetter(FURNISHED_FP_SKUS);
  const selected_addon_keys = [
    gardenLetter,
    texturedLetter,
    furnishedLetter,
  ].filter(Boolean);

  const sorted_keys = KEY_ORDER.filter((key) => {
    const selected = selected_addon_keys.includes(key);
    const pair_selected =
      key.slice(-1) === "R" &&
      selected_addon_keys.includes(key.slice(0, 1) + "M");
    return selected && !pair_selected;
  });
  const image_key = ["FP", ...sorted_keys].join("_");
  const image =
    PREMIUM_FP_ADDON_IMAGES[image_key] || PREMIUM_FP_ADDON_IMAGES.FP;
  return image;
};

export const PremiumAddons = () => {
  const image_src = useSelectedAddons();
  const {
    footerHeight: { order_form },
  } = useFormDetailsState();
  return (
    <div className="mb-5">
      {!isInternal() && (
        <InlineCheckbox
          sku="premium_fp_addons"
          label="Premium Floor Plan Add-Ons"
          description="Add flooring, gardens, and furnishings in black and white or rendered in full colour to your Basic Floor Plan with speedy next-day turnaround time. Select your products to see a sample below."
          labelClassName="h2 text-black"
          isNew
        />
      )}
      <AddOnSection
        show_skus={["premium_fp_addons"]}
        addon_skus={[
          ...PREMIUM_FP_ADDON_SKUS,
          ...Object.keys(SKU_GROUPS).map((key) => `premium_fp_addon_${key}`),
        ]}
      >
        <Row>
          <Col xs={12} sm={4} className="mb-4">
            {map(SKU_GROUPS, (group, key) => {
              const key_name = `premium_fp_addon_${key}`;
              return (
                <AddonGroup key={key_name} group={group} key_name={key_name} />
              );
            })}
            <FurnishedFpProduct />
          </Col>
          <Col xs={12} sm={8}>
            {Object.values(PREMIUM_FP_ADDON_IMAGES).map((url) => {
              return (
                <Image
                  key={url}
                  src={url}
                  style={{
                    maxHeight: `calc(100vh - 3rem - ${order_form}px)`,
                    maxWidth: "100%",
                    position: "sticky",
                    top: 0,
                    display: url === image_src ? "inline" : "none",
                  }}
                />
              );
            })}
          </Col>
        </Row>
      </AddOnSection>
    </div>
  );
};

const AddonGroup = ({ group, key_name }) => {
  const { data: products } = useProducts();
  console.log("❗️ - AddonGroup - products:", products);
  const values = useWatch({ name: group.skus, exact: true });
  const selected_sku =
    group.skus[values.findIndex((v) => !!v)] || group.skus[0];
  const selected_product = products[selected_sku];

  const selected_price = useSetPriceBySqft(selected_sku, 1000);
  const { isNotSmall } = useWindowSize();

  return (
    <div className="mb-4">
      <div className="mb-3">
        <BlockCheckbox
          sku={key_name}
          label={selected_product?.name.split(" - ")[0]}
          description={selected_product?.description}
          custom_price={selected_price}
          defaultValue={!isInternal()}
          short_labels={!isNotSmall}
          col={false}
        />
      </div>
      <AddOnSection show_skus={[key_name]} addon_skus={group.skus} hideInternal>
        {group.skus.map((sku, index) => (
          <Button
            key={sku}
            sku={sku}
            defaultValue={index === 0}
            label={(label, sku) => {
              const product = products[sku];
              return `${label.replace(group.name_prefix, "")} $${
                product.price
              }/sqft`;
            }}
          />
        ))}
      </AddOnSection>
    </div>
  );
};
